// @flow

import React from 'react';
import Switch from 'react-bootstrap-switch';
import _ from 'lodash';

import Ariane from 'PMT/components/Ariane';
import I18n from 'PMT/i18n/I18n';
import Listener from 'PMT/flux/Listener';
import SortableList from 'PMT/components/SortableList';
import Notify from 'PMT/utils/Notify';
import Utils from 'PMT/utils/Utils';

import AbstractComponent from '../../../../components/AbstractComponent';
import CartModifierAction from '../../../../actions/CartModifierAction';
import CartModifierListStore from '../../../../stores/CartModifierListStore';
import RestaurantStore from '../../../../stores/RestaurantStore';
import RoundButton from '../../../../components/RoundButton';
import RouteAction from '../../../../actions/RouteAction';
import RoutingEnum from '../../../../constants/RoutingEnum';
import SecurityContext from '../../../../utils/permissions/SecurityContext';
import SessionStore from '../../../../stores/SessionStore';

import './style.scss';

export default class PromotionsList extends AbstractComponent {
  constructor() {
    super();

    this.arianeIcon = 'fa-line-chart';
    this.arianeDetails = [
      {
        name: I18n.tr('menu.order'),
        link: RoutingEnum.ORDERS_LIST,
      },
      {
        name: I18n.tr('menu.order.cart_modifiers'),
        link: null,
      },
    ];
  }

  state = {
    cartModifiersState: CartModifierListStore.getState(),
  };

  componentDidMount() {
    if (!RestaurantStore.getRestaurant().haveFeatureOrder()) {
      RouteAction.redirectToFeatureNonActivated();
      return;
    }

    if (!SecurityContext.cartModifiers.canRead()) {
      RouteAction.redirectForce(RoutingEnum.ORDERS_LIST);
    }

    CartModifierListStore.addListener(this.onCartModifierListStoreChanged);

    CartModifierAction.loadCartModifiers(SessionStore.getRestaurantId());
  }

  componentWillUnmount() {
    CartModifierListStore.removeListener(this.onCartModifierListStoreChanged);
  }

  getNotArchivedCartModifiers() {
    const notArchived = this.state.cartModifiersState.data.filter(
      cartModifier => cartModifier.archived !== true
    );

    const sortedByPriority = notArchived.sort((a, b) => {
      return a.priority - b.priority;
    });

    return sortedByPriority;
  }

  onCartModifierListStoreChanged = () => {
    this.setState({
      cartModifiersState: CartModifierListStore.getState(),
    });
  };

  onCartModifierChange = cartModifier => {
    CartModifierAction.putCartModifier(
      RestaurantStore.getRestaurantId(),
      cartModifier.copy()
    );
  };

  onEdit = cartModifierId => {
    if (SecurityContext.cartModifiers.canEdit()) {
      RouteAction.redirectTo(RoutingEnum.ORDERS_CART_MODIFIERS_EDIT, {
        cartModifierId,
      });
    }
  };

  onToggleActivation = cartModifier => {
    if (SecurityContext.cartModifiers.canEdit()) {
      cartModifier.enabled = !cartModifier.enabled;
      this.onCartModifierChange(cartModifier);
    }
  };

  onDeleteCartModifier = cartModifier => {
    Notify.confirmDeleteWithTitle(
      I18n.tr('order.cart_modifiers.archive.confirm.title'),
      I18n.tr('order.cart_modifiers.archive.confirm.message'),
      result => {
        if (result) {
          cartModifier.archived = !cartModifier.archived;
          this.onCartModifierChange(cartModifier);
        }
      }
    );
  };

  onListOrderChange = (newOrderedList: Array) => {
    console.log('onListOrderChange', newOrderedList);
    const ids = newOrderedList.map(item => item.id);
    CartModifierAction.putCartModifiersPriorities(
      RestaurantStore.getRestaurantId(),
      ids
    );
  };

  renderRow = cartModifier => {
    return (
      <div className="pointer row">
        <div className="col-sm-3" onClick={() => this.onEdit(cartModifier.id)}>
          {cartModifier.name}
        </div>
        <div className="col-xs-5" onClick={() => this.onEdit(cartModifier.id)}>
          {cartModifier.description}
        </div>
        {SecurityContext.cartModifiers.canEdit() && (
          <div className="col-xs-2 text-center">
            <Switch
              key={Utils.generateUID()}
              bsSize="mini"
              onChange={() => this.onToggleActivation(cartModifier)}
              value={cartModifier.enabled}
            />
          </div>
        )}
        {SecurityContext.cartModifiers.canEdit() && (
          <div
            className="pointer text-center col-xs-1"
            onClick={() => this.onDeleteCartModifier(cartModifier)}
          >
            <i className="fa fa-trash" />
          </div>
        )}
        {SecurityContext.cartModifiers.canEdit() && (
          <div className="pointer text-center col-xs-1">
            <SortableList.DragHandle />
          </div>
        )}
      </div>
    );
  };

  renderList() {
    return (
      <div className="table_container table_container_hover top-buffer-50 col-xs-12">
        <div className="thead col-xs-12">
          <div className="row">
            <div className="col-sm-3">
              {I18n.tr('order.cart_modifiers.list.name')}
            </div>

            <div className="col-xs-5">
              {I18n.tr('order.cart_modifiers.list.description')}
            </div>

            {SecurityContext.cartModifiers.canEdit() && (
              <div className="col-xs-2 text-center">
                {I18n.tr('order.cart_modifiers.list.activate')}
              </div>
            )}

            {SecurityContext.cartModifiers.canEdit() && (
              <div className="col-xs-1 text-center">
                {I18n.tr('order.cart_modifiers.list.delete')}
              </div>
            )}

            {SecurityContext.cartModifiers.canEdit() && (
              <div className="col-xs-1 text-center">
                {I18n.tr('order.cart_modifiers.list.priority')}
              </div>
            )}
          </div>
        </div>
        <SortableList
          items={this.getNotArchivedCartModifiers()}
          onChange={this.onListOrderChange}
          renderer={this.renderRow}
          displayDragIcon={false}
        />
      </div>
    );
  }

  render() {
    const cartModifiersState = this.state.cartModifiersState;

    if (
      _.isEqual(CartModifierListStore.getInitialState(), cartModifiersState)
    ) {
      return this.renderLoading();
    }

    return (
      <div className="order_cart_modifiers">
        <Ariane icon={this.arianeIcon} details={this.arianeDetails} />

        {SecurityContext.cartModifiers.canEdit() && (
          <RoundButton
            text={I18n.tr('order.cart_modifiers.add')}
            onClick={() => {
              RouteAction.redirectTo(RoutingEnum.ORDERS_CART_MODIFIERS_NEW);
            }}
          />
        )}

        {cartModifiersState.isFetching ? (
          this.renderOnLoadingContent()
        ) : !_.isEmpty(cartModifiersState.data) ? (
          this.renderList()
        ) : (
          <div className="no_data">
            <div className="col-xs-6 col-xs-offset-3 text-center">
              <i className="icon-smiley-down" />
              <p className="top-buffer-30">
                {I18n.tr('order.cart_modifiers.no_data')}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }
}
